import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronRight, Mail, Send, Eye, Users } from "lucide-react";
import { API, api } from "@/config";
import { usePageTitle } from "@/hooks/usePageTitle";

const EmailBroadcastPage = ({ isAdmin, adminPassword }) => {
  

  usePageTitle('Email Broadcast');
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [targetList, setTargetList] = useState('general');
  const [mailingLists, setMailingLists] = useState([]);



  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const editorRef = useRef(null);
  
  // Fetch mailing lists once on mount
  useEffect(() => {
    fetchMailingLists();
    fetchDrafts();
  }, []);
  
  // Fetch subscriber count when targetList changes
  useEffect(() => {
    if (targetList) {
      fetchSubscriberCount();
    }
  }, [targetList]);
  


  // Handle paste - let browser handle Word formatting natively
  const handlePaste = (e) => {
    // Don't prevent default - let browser handle rich text paste
    // Just update state after paste completes
    setTimeout(() => {
      if (editorRef.current) {
        setContent(editorRef.current.innerHTML);
      }
    }, 0);
  };

  // Handle input in the editor
  const handleEditorInput = () => {
    if (editorRef.current) {
      // Get HTML - browser already handles formatting
      setContent(editorRef.current.innerHTML);
    }
  };

  // Format commands
  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleEditorInput();
  };

  const insertLink = () => {
    const url = prompt('Enter URL:', 'https://');
    if (url) {
      formatText('createLink', url);
    }
  };
  
  const clearFormatting = () => {
    if (editorRef.current) {
      // Get plain text and convert newlines to br tags
      const text = editorRef.current.innerText;
      const lines = text.split('\n');
      editorRef.current.innerHTML = lines.join('<br>');
      setContent(editorRef.current.innerHTML);
    }
  };



  useEffect(() => {
    if (targetList) {
      fetchSubscriberCount();
    }
  }, [targetList]);

  const fetchMailingLists = async () => {
    try {
      const res = await api.get(`/newsletter/lists`);
      setMailingLists(res.data);
      if (res.data.length > 0 && !targetList) {
        setTargetList(res.data[0].slug);
      }
    } catch (err) {
      console.error('Failed to fetch mailing lists');
    }
  };

  const fetchSubscriberCount = async () => {
    try {
      const res = await api.get(`/newsletter/subscribers?status=confirmed&list_filter=${targetList}`);
      setSubscriberCount(res.data.length);
    } catch (err) {
      console.error('Failed to fetch subscriber count');
    }
  };

  const fetchDrafts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await api.get('/newsletter/drafts?token=' + token);
      setDrafts(res.data.drafts || []);
    } catch (err) {
      console.error('Failed to fetch drafts');
    }
  };

  const handleSaveDraft = async () => {
    if (!subject && !content) {
      toast.error('Nothing to save');
      return;
    }
    try {
      const token = localStorage.getItem('adminToken');
      const payload = { subject, content, target_list: targetList };
      if (currentDraftId) {
        await api.put('/newsletter/drafts/' + currentDraftId + '?token=' + token, payload);
        alert('✅ Draft updated!');
      } else {
        const res = await api.post('/newsletter/drafts?token=' + token, payload);
        setCurrentDraftId(res.data.id);
        alert('✅ Draft saved!');
      }
      fetchDrafts();
    } catch (err) {
      toast.error('Failed to save draft');
    }
  };

  const handleLoadDraft = (draft) => {
    setSubject(draft.subject || '');
    setTargetList(draft.target_list || 'general');
    setCurrentDraftId(draft.id);
    setShowDrafts(false);
    // Set editor content after render
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = draft.content || '';
        setContent(draft.content || '');
      }
    }, 100);
  };

  const handleDeleteDraft = async (draftId) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await api.delete('/newsletter/drafts/' + draftId + '?token=' + token);
      fetchDrafts();
      if (currentDraftId === draftId) {
        setCurrentDraftId(null);
      }
    } catch (err) {
      toast.error('Failed to delete draft');
    }
  };

  const handleNewDraft = () => {
    setSubject('');
    setContent('');
    setCurrentDraftId(null);
    if (editorRef.current) editorRef.current.innerHTML = '';
  };

  const handleSendTest = async () => {
    if (!subject || !content) {
      toast.error('Please fill in subject and content');
      return;
    }
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    setLoading(true);
    try {
      await api.post('/newsletter/broadcast', {
        subject,
        html_content: content,
        target_lists: [targetList],
        test_email: testEmail
      });
      alert(`✅ Test email sent to ${testEmail}!`);
      
      // Clear test email field after success
      setTestEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    }
    setLoading(false);
  };

  const handleSendBroadcast = async () => {
    if (!subject || !content) {
      toast.error('Please fill in subject and content');
      return;
    }
    
    // Check for subscribers BEFORE sending
    if (subscriberCount === 0) {
      toast.error('No subscribers found! There are no confirmed subscribers in this mailing list.');
      return;
    }
    
    const listName = mailingLists.find(l => l.slug === targetList)?.name || targetList;
    if (!window.confirm(`Are you sure you want to send this email to ${subscriberCount} subscriber${subscriberCount !== 1 ? 's' : ''} in "${listName}" list?`)) {
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post(`/newsletter/broadcast`, {
        subject,
        html_content: content,
        target_lists: [targetList]
      });
      const listName = mailingLists.find(l => l.slug === targetList)?.name || targetList;
      const count = res.data?.sent || subscriberCount;
      alert(`✅ Email sent to ${count} subscriber${count !== 1 ? 's' : ''} in "${listName}"!`);
      setSubject('');
      setContent('');
    } catch (err) {
      alert(`❌ Error: ${err.response?.data?.detail || 'Failed to send broadcast'}`);
    }
    setLoading(false);
  };


  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="email-broadcast-page">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/spoleto/newsletter')}
          className="text-[#0000FF] flex items-center gap-2 mb-8 hover:gap-3 transition-all text-sm"
        >
          <ChevronRight size={16} className="rotate-180" />
          Back to Subscribers
        </button>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Mail size={32} className="text-[#0000FF]" />
            <h1 className="text-3xl md:text-4xl font-bold">Send Email Broadcast</h1>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={targetList}
              onChange={(e) => setTargetList(e.target.value)}
              className="bg-gray-50 border border-gray-200 px-4 py-2 text-sm focus:border-[#0000FF] outline-none"
              data-testid="target-list-select"
            >
              {mailingLists.map(list => (
                <option key={list.id} value={list.slug}>
                  {list.name}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users size={16} />
              <span>{subscriberCount} subscribers</span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Subject */}
          <div className="bg-white border border-gray-200 p-6">
            <label className="block text-sm text-muted-foreground mb-2">Subject Line</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject..."
              className="w-full bg-gray-50 border border-gray-200 px-4 py-3 focus:border-[#0000FF] outline-none"
              data-testid="email-subject-input"
            />
          </div>

          {/* Content Editor */}
          <div className="bg-white border border-gray-200 p-6">
            <label className="block text-sm text-muted-foreground mb-2">Email Content (HTML supported)</label>
            
            {/* Toolbar */}
            <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-gray-200">
              <button
                type="button"
                onClick={() => formatText('bold')}
                className="px-3 py-1 bg-gray-50 hover:bg-white border border-gray-200 text-sm font-bold"
                title="Bold"
              >
                B
              </button>
              <button
                type="button"
                onClick={() => formatText('italic')}
                className="px-3 py-1 bg-gray-50 hover:bg-white border border-gray-200 text-sm italic"
                title="Italic"
              >
                I
              </button>
              <button
                type="button"
                onClick={() => formatText('underline')}
                className="px-3 py-1 bg-gray-50 hover:bg-white border border-gray-200 text-sm underline"
                title="Underline"
              >
                U
              </button>
              <button
                type="button"
                onClick={() => formatText('formatBlock', 'h2')}
                className="px-3 py-1 bg-gray-50 hover:bg-white border border-gray-200 text-sm"
                title="Heading"
              >
                H2
              </button>
              <button
                type="button"
                onClick={insertLink}
                className="px-3 py-1 bg-gray-50 hover:bg-white border border-gray-200 text-sm text-[#0000FF]"
                title="Insert Link"
              >
                Link
              </button>
              <button
                type="button"
                onClick={() => formatText('insertUnorderedList')}
                className="px-3 py-1 bg-gray-50 hover:bg-white border border-gray-200 text-sm"
                title="Bullet List"
              >
                • List
              </button>
            </div>

            <div
              ref={editorRef}
              contentEditable
              onInput={handleEditorInput}
              onPaste={handlePaste}
              className="w-full bg-gray-50 border border-gray-200 px-4 py-3 focus:border-[#0000FF] outline-none min-h-[300px] text-sm max-w-none overflow-auto text-gray-900"
              data-testid="email-content-input"
              style={{ whiteSpace: 'pre-wrap', color: '#1f2937' }}
              data-placeholder="Write your email content here... Paste from Word to preserve formatting."
            />
            <style>{`
              [data-placeholder]:empty:before {
                content: attr(data-placeholder);
                color: #666;
                pointer-events: none;
              }
              [contenteditable] * {
                color: #ffffff !important;
              }
              [contenteditable] a {
                color: #0000FF !important;
              }
            `}</style>
            <p className="text-xs text-muted-foreground mt-2">
              Tip: Paste directly from Word to keep formatting. Use the toolbar for additional formatting.
            </p>
          </div>

          {/* Preview */}
          <div className="bg-white border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm text-muted-foreground">Preview</label>
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="text-[#0000FF] text-sm flex items-center gap-2 hover:underline"
              >
                <Eye size={16} />
                {showPreview ? 'Hide Preview' : 'Show Preview'}
              </button>
            </div>
            
            {showPreview && (
              <div className="bg-white border border-gray-200 p-6 rounded">
                <div className="text-center border-b border-gray-200 pb-4 mb-4">
                  <img 
                    src="/logo.png" 
                    alt="Logo" 
                    className="h-16 mx-auto mb-2"
                  />
                  <p className="text-[#0000FF] text-xs uppercase tracking-widest">GetBenefitSavings</p>
                </div>
                <h2 className="text-xl font-bold mb-4">{subject || 'Email Subject'}</h2>
                <div 
                  className="prose prose-invert prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: content || '<p class="text-muted-foreground">Your email content will appear here...</p>' }}
                />
              </div>
            )}
          </div>

          {/* Test Email */}
          <div className="bg-white border border-gray-200 p-6">
            <label className="block text-sm text-muted-foreground mb-2">Send Test Email First</label>
            <div className="flex gap-4">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-gray-50 border border-gray-200 px-4 py-3 focus:border-[#0000FF] outline-none"
                data-testid="test-email-input"
              />
              <button
                type="button"
                onClick={handleSendTest}
                disabled={loading || !subject || !content || !testEmail}
                className="btn-secondary flex items-center gap-2 disabled:opacity-50"
                data-testid="send-test-btn"
              >
                <Send size={16} />
                Send Test
              </button>
            </div>
          </div>

          {/* Send Broadcast */}
          <div className="flex flex-col gap-4">
            {subscriberCount === 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-500 px-4 py-3 text-sm flex items-center gap-2">
                <Users size={16} />
                No subscribers in this list. Add subscribers before sending a broadcast.
              </div>
            )}
            {/* Drafts Panel */}
            <div className="bg-white border border-gray-200 p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  onClick={() => setShowDrafts(!showDrafts)}
                  className="text-sm font-medium text-gray-700 flex items-center gap-1"
                >
                  📄 Saved Drafts ({drafts.length})
                  <svg className={`w-4 h-4 transition-transform ${showDrafts ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                <button type="button" onClick={handleNewDraft} className="text-xs text-blue-600 hover:underline">+ New</button>
              </div>
              {showDrafts && drafts.length > 0 && (
                <div className="space-y-2 mt-3 max-h-48 overflow-y-auto">
                  {drafts.map(draft => (
                    <div key={draft.id} className={`flex items-center justify-between p-2 rounded text-sm ${currentDraftId === draft.id ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 hover:bg-gray-100'}`}>
                      <button type="button" onClick={() => handleLoadDraft(draft)} className="text-left flex-1 truncate">
                        <span className="font-medium">{draft.subject || '(No subject)'}</span>
                        <span className="text-gray-400 ml-2 text-xs">{new Date(draft.updated_at).toLocaleDateString()}</span>
                      </button>
                      <button type="button" onClick={() => handleDeleteDraft(draft.id)} className="text-red-400 hover:text-red-600 ml-2 text-xs">✕</button>
                    </div>
                  ))}
                </div>
              )}
              {showDrafts && drafts.length === 0 && (
                <p className="text-xs text-gray-400 mt-2">No saved drafts yet</p>
              )}
            </div>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={handleSaveDraft}
                className="btn-secondary flex items-center gap-2"
              >
                💾 {currentDraftId ? 'Update Draft' : 'Save Draft'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/spoleto/newsletter')}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendBroadcast}
                disabled={loading || !subject || !content || subscriberCount === 0}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="send-broadcast-btn"
              >
                <Mail size={16} />
                {loading ? 'Sending...' : subscriberCount === 0 ? 'No Subscribers' : `Send to ${subscriberCount} Subscriber${subscriberCount !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailBroadcastPage;
